import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { getFocusedItemId, HaNViewStoreContext, selectSelection, useHaNView } from "@lib/logic";
import { useRequiredContext } from "@lib/utils";
import { EmptyListPlaceholder } from "@molecules";

import { useFitNodeInView } from "../map/useFitNodeInView";
import { FloorSuggestionTile } from "../shared/FloorSuggestionTile";
import { HaNCellSkeleton } from "../shared/HaNCellSkeleton";
import { HaNSlider } from "../shared/HaNSlider/HaNSlider";
import { useOpenResult } from "../shared/useOpenResult";
import { useOpenSuggestedResult } from "../shared/useOpenSuggestedResult";
import { HaNContext } from "../shell/HaNContext";
import { FacilityCategoryEntryCell } from "./components/FacilityCategoryEntryCell";
import { useFacilitiesModel } from "./useFacilitiesModel";

export const FacilitiesCarousel = () => {
  const { tab } = useRequiredContext(HaNContext);

  if (tab !== "facilities") return null;

  return <FacilitiesCarouselContent />;
};

// The open category's entries on the floor, or all of its facilities without one. When the floor has none, the nearest
// floors above and below that have some, each as its tile and its entries. It centres on the selected entry, or the
// first one.
const FacilitiesCarouselContent = () => {
  const { t } = useTranslation();
  const { isLoading, entriesOnFloor, otherFloors } = useFacilitiesModel();
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const selectedId = useHaNView(state => selectSelection("facilities")(state)?.id ?? null);
  const openResult = useOpenResult();
  const openSuggestedResult = useOpenSuggestedResult();
  const fitNodeInView = useFitNodeInView();

  if (isLoading) {
    return (
      <HaNSlider.Card className="items-center">
        <HaNCellSkeleton />
      </HaNSlider.Card>
    );
  }

  const suggestions =
    entriesOnFloor.length > 0
      ? []
      : [otherFloors.find(f => f.direction === "above"), otherFloors.find(f => f.direction === "below")].filter(
          floorGroup => floorGroup !== undefined,
        );

  if (entriesOnFloor.length === 0 && suggestions.length === 0) {
    return (
      <HaNSlider.Card>
        <EmptyListPlaceholder text={t("hereAndNow:NoneOnFloor")} />
      </HaNSlider.Card>
    );
  }

  const entryIds = entriesOnFloor.map(e => e.id);
  const focusedIndex = entryIds.indexOf(getFocusedItemId(entryIds, selectedId) ?? "");

  // Swiping to one of the floor's entries selects it, like tapping it on the map. Other floors aren't on the map.
  const focusSlide = (index: number) => {
    const entry = entriesOnFloor[index];

    if (!entry) return;

    viewStore.select("facilities", { kind: entry.kind, id: entry.id });

    fitNodeInView(entry.id);
  };

  return (
    <HaNSlider
      focusedIndex={focusedIndex}
      onFocusChange={focusSlide}>
      {entriesOnFloor.length > 0 ? (
        entriesOnFloor.map(entry => {
          return (
            <HaNSlider.Slide key={entry.id}>
              <HaNSlider.CardButton onClick={() => openResult(entry.kind, entry)}>
                <FacilityCategoryEntryCell entry={entry} />
              </HaNSlider.CardButton>
            </HaNSlider.Slide>
          );
        })
      ) : (
        <HaNSlider.Slide>
          <HaNSlider.Card>
            <EmptyListPlaceholder
              text={t("hereAndNow:NoneOnFloor")}
              className="justify-center p-2 [&>svg]:size-14"
            />
          </HaNSlider.Card>
        </HaNSlider.Slide>
      )}
      {/* Swiper only finds slides among its children and fragments, so these are elements rather than a component */}
      {suggestions.map(({ floorId, direction, items }) => {
        return (
          <Fragment key={floorId}>
            <HaNSlider.Slide>
              <HaNSlider.Card>
                <FloorSuggestionTile
                  floorId={floorId}
                  direction={direction}
                />
              </HaNSlider.Card>
            </HaNSlider.Slide>

            {items.map(entry => {
              return (
                <HaNSlider.Slide key={entry.id}>
                  <HaNSlider.CardButton onClick={() => openSuggestedResult(entry.kind, entry, direction)}>
                    <FacilityCategoryEntryCell entry={entry} />
                  </HaNSlider.CardButton>
                </HaNSlider.Slide>
              );
            })}
          </Fragment>
        );
      })}
    </HaNSlider>
  );
};
